import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_URL, END_POINTS } from '../constants/url';
import { STORAGE_KEYS, getFromStorage, removeFromStorage, setToStorage, showError } from '../constants';

const apiService = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface ErrorResponse {
  message: string;
}

// --- Token refresh state ---
let refreshPromise: Promise<string> | null = null;

const clearAuthAndRedirect = () => {
  removeFromStorage(STORAGE_KEYS.token);
  removeFromStorage(STORAGE_KEYS.refreshToken);
  removeFromStorage(STORAGE_KEYS.credentials);
  removeFromStorage(STORAGE_KEYS.userData);
  window.location.replace('/');
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getFromStorage(STORAGE_KEYS.refreshToken) as string | null;
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Use a separate axios instance to avoid interceptor loops
  const refreshClient = axios.create({ baseURL: API_URL });
  const response = await refreshClient.get(END_POINTS.refreshToken, {
    headers: { Authorization: `Bearer ${refreshToken}` },
  });

  const newToken = response?.data?.data?.token;
  const newRefreshToken = response?.data?.data?.refreshToken;

  if (!newToken) {
    throw new Error('Token refresh returned no token');
  }

  setToStorage(STORAGE_KEYS.token, newToken);
  if (newRefreshToken) {
    setToStorage(STORAGE_KEYS.refreshToken, newRefreshToken);
  }

  return newToken;
};

// --- Request interceptor: attach token to every request ---
apiService.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getFromStorage(STORAGE_KEYS.token) as string | null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Response interceptor: handle 401 with token refresh ---
apiService.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // If a refresh is already in progress, wait for it
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken();
        }

        const newToken = await refreshPromise;

        // Update the failed request's auth header and retry
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiService(originalRequest);
      } catch (refreshError) {
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        refreshPromise = null;
      }
    }

    // Non-401 errors or retry failures
    if (error.response) {
      showError(error?.response?.data?.message || '');
    }

    return Promise.reject(error);
  }
);

// --- Response handler ---
const handleResponse = <T>(response: AxiosResponse<T>): T => {
  if (response.status === 200 || response.status === 201) {
    return response.data;
  } else {
    throw new Error(`Request failed with status ${response.status}`);
  }
};

// --- API methods ---
const get = async <T>(url: string, params: any = {}): Promise<T | undefined> => {
  const response = await apiService.get<T>(url, { params });
  return handleResponse(response);
};

const post = async <T>(url: string, data: any): Promise<T | undefined> => {
  const response = await apiService.post<T>(url, data);
  return handleResponse(response);
};

const put = async <T>(url: string, data: any): Promise<T | undefined> => {
  const response = await apiService.put<T>(url, data);
  return handleResponse(response);
};

const del = async <T>(url: string): Promise<T | undefined> => {
  const response = await apiService.delete<T>(url);
  return handleResponse(response);
};

const formDataPost = async <T>(url: string, data: any): Promise<T | undefined> => {
  const response = await apiService.post<T>(url, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return handleResponse(response);
};

const formDataPut = async <T>(url: string, data: any): Promise<T | undefined> => {
  const response = await apiService.put<T>(url, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return handleResponse(response);
};

export { get, post, put, del, formDataPost, formDataPut };
